<script setup lang="ts">
    import { reactive, onMounted } from 'vue';
    import { type Survey } from './SurveysTypes.d';
    import { loadSurvey } from './Surveys.access';
    import Loading from '@shared/components/Loading.vue';
    import { formatDateTime } from '@cwd/Shared/utils/formatDateTime';
    import { copyToClipboard } from '@views/Web/copyToClipboard';
    import NavCreate from '@cwd/Navigation/NavCreate.vue';
    import ShareIcons from '@cwd/Shared/components/ShareIcons.vue';
    import PopoverSurvey from '@shared/components/PopoverSurvey.vue';

    const props = defineProps<{
        surveyId: string;
    }>();

    const state = reactive<{
        survey: null | Survey;
        isBusy: boolean;
        isScheduled: boolean;
    }>({
        survey: null,
        isBusy: true,
        isScheduled: false,
    });

    onMounted(async () => {
        state.survey = await loadSurvey(props.surveyId);
        state.isBusy = false;
        state.isScheduled = isScheduled(state.survey.Start);
    });

    const iframeCode = () => {
        return '<iframe width="100%" height="600" src="' + state.survey!.Url + '" frameborder="0"></iframe>';
    };

    const isScheduled = (dateVal: string) => {
        const start = new Date(dateVal);
        const now = new Date();

        return start > now;
    };

</script>

<template>
    <div v-if="state.survey">
        <section class="hero-bg p-lg-all c-white">
            <div class="max-900 container-fluid">
                <h1 class="strong">{{ $Resources.SendingHeading }}</h1>

                <p class="text-lg m-lg-b">
                    {{ $Resources.PublishedSurveyYourSurveyLabel }}
                    <strong>"{{ state.survey.Title }}"</strong>
                    {{
                        state.isScheduled
                            ? $Resources.PublishedSurveyScheduledLabel.replace('{Start}', formatDateTime(state.survey.Start).toString())
                            : $Resources.PublishedSurveyPublishedLabel
                    }}
                </p>

                <div class="form-group d-flex flex-wrap gap-sm">
                    <a v-if="!state.isScheduled" role="button" class="btn btn-cta" :href="state.survey.Url" target="_blank" rel="noopener">
                        {{ $Resources.PublishedSurveyViewSurveyBtn }}
                        <i class="fa fa-chevron-right" aria-hidden="true"></i>
                    </a>
                    <router-link
                        role="button"
                        class="btn btn-default-transparent"
                        :to="{ name: 'SurveyStatistics', params: { surveyId: state.survey.SurveyId } }">
                        {{ $Resources.PublishedSurveySeeStatisticsLabel }}
                        <i class="fa fa-chevron-right" aria-hidden="true"></i>
                    </router-link>
                    <router-link
                        role="button"
                        class="btn btn-default-transparent"
                        :to="{ name: 'IssuesNew', query: { surveyId: state.survey.SurveyId } }">
                        {{ $Resources.SurveyCommunicationSendInvitationButton }}
                        <i class="fa fa-chevron-right" aria-hidden="true"></i>
                    </router-link>
                </div>
            </div>
        </section>

        <section class="m-xl-b">
            <div class="max-900 container-fluid m-lg-t m-sm-b">
                <h2 class="h3 m-lg-b">{{ $Resources.SendingDetailedInfo }}</h2>

                <div class="table-responsive">
                    <table class="table">
                        <tbody>
                            <tr>
                                <td class="text-nowrap text-right autofit">{{ $Resources.DescriptionLabel }}:</td>
                                <td class="strong">
                                    <span v-show="state.survey.SurveyName === null">{{ $Resources.PublishedSurveyValueNotSet }}</span>
                                    <span v-show="state.survey.SurveyName !== null">{{ state.survey.SurveyName }}</span>
                                </td>
                            </tr>
                            <tr>
                                <td class="text-nowrap text-right">{{ $Resources.PublishedSurveyStartLabel }}:</td>
                                <td class="strong">
                                    <span v-show="state.survey.Start === null">{{ $Resources.PublishedSurveyValueNotSet }}</span>
                                    {{ formatDateTime(state.survey.Start) }}
                                </td>
                            </tr>
                            <tr>
                                <td class="text-nowrap text-right">{{ $Resources.PublishedSurveyEndLabel }}:</td>
                                <td class="strong">
                                    <span v-show="state.survey.End === null">{{ $Resources.PublishedSurveyValueNotSet }}</span>
                                    {{ formatDateTime(state.survey.End) }}
                                </td>
                            </tr>
                            <tr>
                                <td class="text-nowrap text-right">{{ $Resources.PublishedSurveyMaxResponseLimitsLabel }}:</td>
                                <td class="strong">
                                    <span v-show="state.survey.ResponseLimit === 0">{{ $Resources.PublishedSurveyValueNotSet }}</span>
                                    <span v-show="state.survey.ResponseLimit > 0">{{ state.survey.ResponseLimit }}</span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <ShareIcons :id="state.survey.SurveyId" :label="$Resources.ShareLabel" size="large" type="Surveys"></ShareIcons>

                <h2 class="h3 m-lg-b">{{ $Resources.PublishedSurveyNextStepHeading }}</h2>

                <div class="card-1">
                    <h3 class="h4">{{ $Resources.PublishedSurveyLinkToSurveyInstructionsHeading }}</h3>

                    <p class="m-lg-b">
                        <a href="javascript:void(0)" :data-helpcenter-article="$Resources.PublishedSurveyLinkToSurveyManualId">
                            {{ $Resources.PublishedSurveyLinkToSurveyInstructionsLabel }}
                        </a>
                    </p>
                </div>

                <div class="card-1">
                    <h3 class="h4">{{ $Resources.PublishedSurveyPublicUrl }}</h3>

                    <div class="form-group">
                        <pre>{{ state.survey.Url }}</pre>
                        <button type="button" class="btn btn-default btn-xs" @click="copyToClipboard(state.survey.Url)">
                            {{ $Resources.CopyToClipBoardBtn }}
                        </button>
                    </div>
                </div>

                <div class="card-1">
                    <h3 class="h4">{{ $Resources.PublishedSurveyEmbedSurveyWithPluginHeading }}</h3>

                    <p class="m-lg-b">
                        <a href="javascript:void(0)" :data-helpcenter-article="$Resources.PublishedSurveyEmbedSurveyWithPluginManualId">
                            {{ $Resources.PublishedSurveyLinkToSurveyInstructionsLabel }}
                        </a>
                    </p>
                </div>

                <div class="card-1">
                    <h3 class="h4">{{ $Resources.PublishedSurveyEmbedAtWebpageLabel }}</h3>

                    <div class="form-group">
                        <pre>{{ iframeCode() }}</pre>
                        <button type="button" class="btn btn-default btn-xs" @click="copyToClipboard(iframeCode())">
                            {{ $Resources.CopyToClipBoardBtn }}
                        </button>
                    </div>
                </div>

                <h2 class="h3 m-lg-b">{{ $Resources.CreateNewHeading }}</h2>

                <NavCreate></NavCreate>
            </div>
        </section>
    </div>

    <PopoverSurvey id="11284780-0661-4fad-a2e5-8b19d301a825"></PopoverSurvey>

    <Loading :show="state.isBusy" loadingStyle="progress-bar" :fixed="true"></Loading>
</template>

<style scoped lang="less"></style>

<style lang="less"></style>
